import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import DriveEtaOutlinedIcon from '@mui/icons-material/DriveEtaOutlined';
import LoopIcon from '@mui/icons-material/Loop';
import { styled } from '@mui/material/styles';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import { MapContainer } from 'react-leaflet';
import MapWithNavigation from '../MapWithNavigation';
import * as lookupSlice from '../../../data/lookup.slice';
import * as reservationSlice from '../../../data/reservation.slice';
import * as providerSlice from '../../../data/provider.slice';
import * as clientSlice from '../../../data/client.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  Reservation,
  StandingOrder,
  TripLegAddress,
  TripLegType,
} from '../../../data/models/reservation.model';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import Directions from '../leaflet/Directions';
import dateUtils from '../../../services/DateUtils';
import TabPanel from '../../TabPanel';
import PassengerBenefits from '../passengers/PassengerBenefits';
import { ServicePlan } from '../../../data/models/service-plan.model';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { Passenger } from '../../../data/models/passenger.model';
import { phoneUtils } from '../../../services/phoneUtils';
import { useAlternateContactType } from '../../../hooks/display/useAlternateContactType';
import { usePortalName } from '../../../hooks/usePortalName';

const PREFIX = 'ReservationSummary';

const classes = {
  container: `${PREFIX}-container`,
  previous: `${PREFIX}-previous`,
  title: `${PREFIX}-title`,
  content: `${PREFIX}-content`,
  benefitmap: `${PREFIX}-benefitmap`,
  actions: `${PREFIX}-actions`,
  row: `${PREFIX}-row`,
  topSpace: `${PREFIX}-topSpace`,
  iconContainer: `${PREFIX}-iconContainer`,
  textContainer: `${PREFIX}-textContainer`,
  noteContainer: `${PREFIX}-noteContainer`,
  note: `${PREFIX}-note`,
  mapContainer: `${PREFIX}-mapContainer`,
  bgdots: `${PREFIX}-bgdots`,
  padBottom: `${PREFIX}-padBottom`,
  marginBottom: `${PREFIX}-marginBottom`,
  map: `${PREFIX}-map`,
  strong: `${PREFIX}-strong`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },

  [`& .${classes.previous}`]: {
    display: 'flex',
    alignItems: 'end',
    textDecoration: 'none',
    textTransform: 'uppercase',
    fontSize: 14,
    fontWeight: 600,
    fontStyle: 'normal',
    letterSpacing: 1.5,
    lineHeight: '157%',
    marginLeft: theme.spacing(-1),
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
  },

  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingBottom: theme.spacing(5),
    gap: theme.spacing(2),
    flex: 1,
    width: '-webkit-fill-available',
  },

  [`& .${classes.benefitmap}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    flex: 1,
    width: '-webkit-fill-available',
  },

  [`& .${classes.actions}`]: {
    display: 'flex',
    gap: theme.spacing(2),
  },

  [`& .${classes.row}`]: {
    display: 'flex',
  },

  [`& .${classes.topSpace}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.iconContainer}`]: {
    display: 'inline-block',
    width: 24,
    textAlign: 'center',
    marginRight: theme.spacing(1.5),
  },

  [`& .${classes.bgdots}`]: {
    height: 28,
    backgroundImage: `url('/1dot.png')`,
    backgroundRepeat: 'repeat-y',
    backgroundPositionX: 'center',
  },

  [`& .${classes.padBottom}`]: {
    paddingBottom: theme.spacing(1),
  },

  [`& .${classes.marginBottom}`]: {
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.textContainer}`]: {
    display: 'inline-block',
    lineHeight: '150%',
    letterSpacing: 0.15,
  },

  [`& .${classes.noteContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
  },

  [`& .${classes.note}`]: {
    height: 24,
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    backgroundColor: 'rgba(25, 124, 179, 0.12)',
  },

  [`& .${classes.mapContainer}`]: {
    paddingTop: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingBottom: theme.spacing(5),
  },

  [`& .${classes.map}`]: {
    with: '100%',
    height: 300,
    '& .leaflet-routing-container .leaflet-routing-alternatives-container': {
      display: 'none',
    },
    '& .leaflet-control-container .leaflet-bottom.leaflet-right': {
      display: 'none',
    },

    '& .leaflet-marker-pane .leaflet-marker-icon.pickup_icon': {
      width: 30,
    },

    '& .leaflet-marker-pane .leaflet-marker-icon.dropoff_icon': {
      width: 25,
    },
  },

  [`& .${classes.strong}`]: {
    fontWeight: 600,
  },
}));

const PassengerDetail = ({
  passenger,
  noteBCs,
}: {
  passenger: Passenger;
  noteBCs: string[];
}) => {
  const t = useCommonTranslation();
  const mobile = phoneUtils.getPassengerMobilePhone(passenger.passengerPhones);
  const home = phoneUtils.getPassengerHomePhone(passenger.passengerPhones);
  const passengerNoteTypes = useAppSelector(
    lookupSlice.selectPassengerNoteTypes,
  );
  const notes = noteBCs.map((bc) => {
    return (
      passengerNoteTypes.find((nt) => nt.tripLegPassengerNoteTypeBC === bc)
        ?.tripLegPassengerNoteTypeName || ''
    );
  });
  let phone = '';
  if (mobile) {
    phone = phoneUtils.formatPhone(mobile, false, true);
    if (mobile.hasSMSConsent) {
      phone += ` (${t('reservation.receivesTexts')})`;
    }
  } else if (home) {
    phone = phoneUtils.formatPhone(home, false, true);
  }
  return (
    <>
      <Box className={classes.row}>
        <Box className={classes.iconContainer}>
          <PersonOutlineOutlinedIcon color="action" />
        </Box>
        <Box className={`${classes.textContainer} ${classes.strong}`}>
          {`${passenger.firstName} ${passenger.lastName}`}
        </Box>
      </Box>
      {phone && (
        <Box className={classes.row}>
          <Box className={classes.iconContainer} />
          <Box className={classes.textContainer}>{phone}</Box>
        </Box>
      )}
      {!!notes?.length && (
        <Box className={classes.row}>
          <Box className={classes.iconContainer} />
          <Box className={classes.noteContainer}>
            {notes.map((note, index) => {
              return (
                <Box
                  key={`note-${index}`}
                  className={`${classes.textContainer} ${classes.note}`}>
                  {note}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};

const AdvocateDetail = ({
  type,
  firstName,
  lastName,
  phone,
  sms,
}: {
  type: string;
  firstName: string;
  lastName: string;
  phone: string;
  sms: boolean;
}) => {
  const t = useCommonTranslation();
  const texts = sms ? `(${t('reservation.receivesTexts')})` : '';
  return (
    <>
      <Box className={classes.row}>
        <Box className={classes.iconContainer} />
        <Box
          className={
            classes.textContainer
          }>{`${t('reservation.advocate')} - ${firstName} ${lastName}, ${type}`}</Box>
      </Box>
      <Box className={classes.row}>
        <Box className={classes.iconContainer} />
        <Box
          className={
            classes.textContainer
          }>{`${phoneUtils.formatPhoneString(phone, false, true)} ${texts}`}</Box>
      </Box>
    </>
  );
};

const TripDetail = ({
  tripPurposeUUID,
  pickupAddress,
  dropoffAddress,
  pickupTime,
  isWillCall,
  returnPickupTime,
  tripType,
  appointmentDate,
  pickupPhone,
  dropoffPhone,
  pickupNote,
  dropoffNote,
}: {
  tripPurposeUUID: string;
  pickupAddress: TripLegAddress;
  dropoffAddress: TripLegAddress;
  pickupTime: DateTime;
  isWillCall: boolean;
  returnPickupTime?: DateTime;
  tripType: TripLegType;
  appointmentDate: DateTime;
  pickupPhone: string;
  dropoffPhone: string;
  pickupNote: string;
  dropoffNote: string;
}) => {
  const t = useCommonTranslation();

  const [purposeCode, setPurposeCode] = useState('');

  const passengerClientData = useAppSelector(
    clientSlice.selectPassengerClientData,
  );
  const formattedPickupTime = dateUtils.formatTime(pickupTime);
  const formattedAppointmentDate = isWillCall
    ? appointmentDate.toFormat('L/d/yyyy')
    : appointmentDate.toFormat('L/d/yyyy - h:mm a');
  const isRoundTrip = tripType === TripLegType.RoundTrip;
  const returnPickup = returnPickupTime
    ? dateUtils.formatTime(returnPickupTime)
    : t('reservation.willCallTime');

  useEffect(() => {
    if (passengerClientData?.purposeCodes) {
      const purpose = passengerClientData.purposeCodes.find(
        (pc) => pc.purposeCodeUUID === tripPurposeUUID,
      );
      setPurposeCode(purpose?.name || '');
    }
  }, [passengerClientData, tripPurposeUUID]);

  return (
    <>
      <Box className={`${classes.row} ${classes.topSpace}`}>
        <Box className={classes.iconContainer}>
          <DateRangeOutlinedIcon color="action" />
        </Box>
        <Box className={`${classes.textContainer} ${classes.strong}`}>
          {formattedAppointmentDate}
        </Box>
      </Box>
      <Box className={classes.row}>
        <Box className={classes.iconContainer} />
        <Box className={classes.textContainer}>{purposeCode}</Box>
      </Box>
      <Box className={`${classes.row} ${classes.topSpace}`}>
        <Box className={classes.iconContainer}>
          <ScheduleOutlinedIcon color="action" />
        </Box>
        <Box className={`${classes.textContainer} ${classes.strong}`}>
          {isWillCall
            ? t('reservation.willCall')
            : t('reservation.estimatedPickup', { time: formattedPickupTime })}
        </Box>
      </Box>{' '}
      <Box className={`${classes.row} ${classes.topSpace}`}>
        <Box className={classes.iconContainer}>
          <LocationOnOutlinedIcon color="primary" />
        </Box>
        <Box className={`${classes.textContainer} ${classes.strong}`}>
          {`${pickupAddress?.addressLine1}, ${pickupAddress?.city}, ${pickupAddress?.stateCode}, ${pickupAddress?.zipCode}`}
        </Box>
      </Box>
      {pickupPhone && (
        <Box className={classes.row}>
          <Box
            className={`${classes.iconContainer} ${classes.bgdots} ${!pickupNote ? classes.padBottom : ''}`}
          />
          <Box className={classes.textContainer}>
            {phoneUtils.formatPhoneString(pickupPhone)}
          </Box>
        </Box>
      )}
      {pickupNote && (
        <Box className={classes.row}>
          <Box
            className={`${classes.iconContainer} ${classes.bgdots} ${classes.padBottom}`}
          />
          <Box
            className={`${classes.textContainer} ${classes.note} ${classes.marginBottom}`}>
            {pickupNote}
          </Box>
        </Box>
      )}
      <Box className={classes.row}>
        <Box className={classes.iconContainer}>
          <LocationOnOutlinedIcon color="error" />
        </Box>
        <Box className={`${classes.textContainer} ${classes.strong}`}>
          {`${dropoffAddress?.addressLine1}, ${dropoffAddress?.city}, ${pickupAddress?.stateCode}, ${dropoffAddress?.zipCode}`}
        </Box>
      </Box>
      {dropoffPhone && (
        <Box className={classes.row}>
          <Box
            className={`${classes.iconContainer} ${isRoundTrip ? classes.bgdots : ''} ${!dropoffNote ? classes.padBottom : ''}`}
          />
          <Box className={classes.textContainer}>
            {phoneUtils.formatPhoneString(dropoffPhone)}
          </Box>
        </Box>
      )}
      {dropoffNote && (
        <Box className={classes.row}>
          <Box
            className={`${classes.iconContainer} ${isRoundTrip ? classes.bgdots : ''} ${isRoundTrip ? classes.padBottom : ''}`}
          />
          <Box
            className={`${classes.textContainer} ${classes.note} ${classes.marginBottom}`}>
            {dropoffNote}
          </Box>
        </Box>
      )}
      {isRoundTrip && (
        <>
          <Box className={classes.row}>
            <Box className={classes.iconContainer}>
              <LocationOnOutlinedIcon color="error" />
            </Box>
            <Box className={`${classes.textContainer} ${classes.strong}`}>
              {`${pickupAddress?.addressLine1}, ${pickupAddress?.city}, ${pickupAddress?.stateCode}, ${pickupAddress?.zipCode}`}
            </Box>
          </Box>
          <Box className={classes.row}>
            <Box className={classes.iconContainer} />
            <Box className={classes.textContainer}>{returnPickup}</Box>
          </Box>
        </>
      )}
    </>
  );
};

const AttributeDetail = ({
  metadata,
  requestedProvider,
}: {
  metadata: any;
  requestedProvider?: number;
}) => {
  const t = useCommonTranslation();
  const providers = useAppSelector(providerSlice.selectProviderList);
  let providerName = '';
  if (requestedProvider) {
    providerName =
      providers.items.find(
        (p) => p.transportationProviderID === requestedProvider,
      )?.transportationProviderName || '';
  }
  const ambTripMode = metadata.ambulatory
    ? t('reservation.ambulatory')
    : t('reservation.nonAmbulatory');
  const ambCare = metadata.doorToDoor
    ? t('reservation.doorToDoor')
    : metadata.handToHand
      ? t('reservation.handToHand')
      : t('reservation.curbToCurb');
  let vehicleType = metadata.equipmentSize || '';
  vehicleType = vehicleType + ' ' + (metadata.mobilityAid || '');
  vehicleType = (
    vehicleType +
    ' ' +
    (metadata.vehicleEquipmentType || '')
  ).trim();
  if (vehicleType.length > 0) {
    vehicleType = `${vehicleType} ${t('reservation.accessibleVehicle')}`;
  }

  return (
    <>
      {vehicleType && (
        <Box className={`${classes.row} ${classes.topSpace}`}>
          <Box className={classes.iconContainer}>
            <DriveEtaOutlinedIcon color="action" />
          </Box>
          <Box className={`${classes.textContainer} ${classes.strong}`}>
            {vehicleType}
          </Box>
        </Box>
      )}
      <Box className={`${classes.row} ${!vehicleType ? classes.topSpace : ''}`}>
        <Box className={classes.iconContainer}>
          {!vehicleType && <DriveEtaOutlinedIcon color="action" />}
        </Box>
        <Box className={classes.textContainer}>
          <span>{`${t('reservation.tripMode')}:`}&nbsp;</span>
          <span className={classes.strong}>{`${ambTripMode}, ${ambCare}`}</span>
        </Box>
      </Box>
      {!!providerName && (
        <Box className={classes.row}>
          <Box className={classes.iconContainer} />
          <Box className={classes.textContainer}>
            <span>{`${t('reservation.requestedProvider')}:`}&nbsp;</span>
            <span className={classes.strong}>{providerName}</span>
          </Box>
        </Box>
      )}
    </>
  );
};

const StandingOrderDetail = ({ metadata }: { metadata: any }) => {
  const t = useCommonTranslation();

  // TODO: finish the implementation of this when narrative metadata has the required info
  return (
    <>
      <Box className={`${classes.row} ${classes.topSpace}`}>
        <Box className={classes.iconContainer}>
          <LoopIcon />
        </Box>
        <Box className={classes.textContainer}>
          {t('reservation.standingOrderSummary')}&nbsp;
          <span className={classes.strong}>{'Monday xxxx'}</span>&nbsp;
          {t('reservation.from')}&nbsp;
          <span className={classes.strong}>{'10/10/2025'}</span>&nbsp;
          {t('reservation.to')}&nbsp;
          <span className={classes.strong}>{'10/15/2025'}</span>.
        </Box>
      </Box>
    </>
  );
};

const ReservationDetail = ({
  passenger,
  tripLegs,
  advocateDetailType,
  narrativeMetadata,
  formHook,
}: {
  passenger: Passenger;
  tripLegs: any[];
  advocateDetailType: string;
  narrativeMetadata: any;
  formHook: UseFormReturn<any, any>;
}) => {
  return (
    <Stack>
      <PassengerDetail
        passenger={passenger}
        noteBCs={formHook.watch('PassengerNoteTypes')}
      />
      {formHook.watch('AlternateContact') && (
        <AdvocateDetail
          type={advocateDetailType}
          firstName={formHook.watch('AlternateContactFirstName')}
          lastName={formHook.watch('AlternateContactLastName')}
          phone={formHook.watch('AlternateContactPhone')}
          sms={formHook.watch('AlternateContactAllowSMS')}
        />
      )}
      {tripLegs.map((tripLeg, index) => (
        <TripDetail
          key={`trip-leg-${index}`}
          tripPurposeUUID={tripLeg.tripPurposeUUID}
          tripType={tripLeg.tripType}
          pickupAddress={tripLeg.pickupAddress}
          dropoffAddress={tripLeg.dropoffAddress}
          pickupTime={tripLeg.pickupTime}
          isWillCall={tripLeg.willCall}
          returnPickupTime={tripLeg.returnPickupTime}
          appointmentDate={tripLeg.appointmentDate}
          pickupPhone={tripLeg.pickupPhone}
          dropoffPhone={tripLeg.dropoffPhone}
          pickupNote={tripLeg.pickupNote}
          dropoffNote={tripLeg.dropoffNote}
        />
      ))}
      <AttributeDetail
        metadata={narrativeMetadata}
        requestedProvider={formHook.watch('RequestedTransportationProviderID')}
      />
      {/* TODO: add this in for standing order reservations when the narrative detail is available */}
      {/* <StandingOrderDetail metadata={narrativeMetadata} /> */}
    </Stack>
  );
};

interface ReservationSummaryProps {
  passenger: Passenger;
  reservation: Reservation;
  standingOrder: StandingOrder | undefined;
  tripCount: number;
  formHook: UseFormReturn<any, any>;
  servicePlans: ServicePlan[];
  isSaving: boolean;
  canSaveAsDraft: boolean;
  isPortalReservation: boolean;
  goBack: (evt: any) => void;
  done: () => void;
  saveAsDraft: () => void;
}

export default function ReservationSummary({
  passenger,
  reservation,
  standingOrder,
  tripCount,
  formHook,
  servicePlans,
  isSaving,
  canSaveAsDraft,
  isPortalReservation,
  goBack,
  done,
  saveAsDraft,
}: ReservationSummaryProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  // isPortal is required to determine if the reservation is being made through a portal or not,
  // beacuse isPortalReservation is manually set to true / false based on the origin of the reservation dialog.
  const isPortal = usePortalName() ? true : false;
  const narrative = useAppSelector(reservationSlice.selectNarrative);
  const narrativeMetadata = useAppSelector(
    reservationSlice.selectNarrativeMetadata,
  );

  useEffect(() => {
    if (reservation) {
      dispatch(
        reservationSlice.getReservationNarrativeAsync(
          reservation,
          standingOrder,
        ),
      );
      setTabIndex(isPortal ? 1 : 0);
    }
  }, [reservation, standingOrder]);

  const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = (_evt: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  const alternateContactType = formHook.watch('AlternateContactType');
  const advocateDetailType = useAlternateContactType(alternateContactType);

  const tripLegs = Array(tripCount)
    .fill(0)
    .map((_, step) => {
      const pickupAddress = formHook.watch(
        `MultiLegTrips[${step}].PickupAddressStruct`,
      );
      const dropoffAddress = formHook.watch(
        `MultiLegTrips[${step}].DropoffAddressStruct`,
      );
      const pickupTimeZone = formHook.watch(
        `MultiLegTrips[${step}].PickupTimeZone`,
      );
      const returnWillCall = formHook.watch(
        `MultiLegTrips[${step}].RoundTripWillCall`,
      );
      const returnWillCallTime = formHook.watch(
        `MultiLegTrips[${step}].RoundTripReturnTime`,
      );

      return {
        tripPurposeUUID: formHook.watch(`MultiLegTrips[${step}].TripPurpose`),
        appointmentTime: formHook.watch(
          `MultiLegTrips[${step}].AppointmentTime`,
        ),
        pickupAddress: pickupAddress ? JSON.parse(pickupAddress) : {},
        dropoffAddress: dropoffAddress ? JSON.parse(dropoffAddress) : {},
        pickupTime: formHook.watch(`MultiLegTrips[${step}].PickupTime`),
        pickupTimeZone: pickupTimeZone,
        returnPickupTime: returnWillCall ? undefined : returnWillCallTime,
        appointmentDate: formHook.watch(
          `MultiLegTrips[${step}].AppointmentTime`,
        ),
        willCall: formHook.watch(`MultiLegTrips[${step}].WillCall`),
        tripType: formHook.watch('TripType'),
        pickupPhone: formHook.watch(`MultiLegTrips[${step}].PickupPhone`),
        dropoffPhone: formHook.watch(`MultiLegTrips[${step}].DropoffPhone`),
        pickupNote: formHook.watch(`MultiLegTrips[${step}].PickupNote`),
        dropoffNote: formHook.watch(`MultiLegTrips[${step}].DropoffNote`),
      };
    });

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.content}>
        <Link
          color="primary"
          className={classes.previous}
          href="#"
          onClick={goBack}
          underline="hover">
          <ChevronLeftOutlinedIcon />
          {t('common.back')}
        </Link>
        <Typography className={classes.title}>
          {`${t('reservation.summary')}`}
        </Typography>
        {!isPortal && (
          <>
            <Tabs
              indicatorColor="primary"
              textColor="primary"
              value={tabIndex}
              onChange={handleTabChange}
              variant="standard">
              <Tab label={t('reservation.narrative')} data-testid="tab-one" />
              <Tab label={t('reservation.details')} data-testid="tab-two" />
            </Tabs>
            <TabPanel value={tabIndex} index={0}>
              <Typography
                dangerouslySetInnerHTML={{
                  __html: narrative || '',
                }}></Typography>
            </TabPanel>
            <TabPanel value={tabIndex} index={1}>
              <ReservationDetail
                passenger={passenger}
                tripLegs={tripLegs}
                advocateDetailType={advocateDetailType}
                narrativeMetadata={narrativeMetadata}
                formHook={formHook}
              />
            </TabPanel>
          </>
        )}
        {isPortal && (
          <ReservationDetail
            passenger={passenger}
            tripLegs={tripLegs}
            advocateDetailType={advocateDetailType}
            narrativeMetadata={narrativeMetadata}
            formHook={formHook}
          />
        )}

        <Box className={classes.actions}>
          <Button
            color="primary"
            variant="contained"
            disabled={isSaving}
            onClick={done}>
            {t('common.submit')}
          </Button>
          {!isPortal && (
            <Button
              color="secondary"
              variant="contained"
              disabled={isSaving || !canSaveAsDraft}
              onClick={saveAsDraft}>
              {t('reservation.saveAsDraft')}
            </Button>
          )}
        </Box>
      </Paper>
      <Box className={classes.benefitmap}>
        {!isPortalReservation && (
          <PassengerBenefits
            appointmentDateTime={tripLegs[0].appointmentDate}
            servicePlans={servicePlans}
          />
        )}
        <Paper className={classes.mapContainer}>
          <MapContainer className={classes.map}>
            <MapWithNavigation tripLegs={tripLegs} driverLocation={null} />
            {tripLegs.length > 0 && (
              <Directions
                origin={{
                  lat: tripLegs[0].pickupAddress.latitude || 0,
                  lng: tripLegs[0].pickupAddress.longitude || 0,
                }}
                destination={{
                  lat: tripLegs[0].dropoffAddress.latitude || 0,
                  lng: tripLegs[0].dropoffAddress.longitude || 0,
                }}
              />
            )}
          </MapContainer>
        </Paper>
      </Box>
    </StyledBox>
  );
}
